
import Button from '@/components/atoms/Button.vue';
import Input from '@/components/atoms/Input.vue';
import moment from 'moment';
import { defineComponent } from 'vue';
import { mapActions, mapGetters } from 'vuex';
import PhoneNumber from '@/components/atoms/PhoneNumber.vue';
import IconArrowLeft from '@/components/atoms/IconArrowLeft.vue';
import Logo from '@/components/atoms/Logo.vue';
import Message from '@/components/atoms/Message.vue';
import PasswordInput from '@/components/atoms/PasswordInput.vue';
import Select from '@/components/atoms/Select.vue';
import Switch from '@/components/atoms/Switch.vue';
import { ROUTES_NAMES } from '@/constants';
import { CognitoException } from '@/model/cognito';
import { dateCollector } from '@/model/date';
import { AppStatus } from '@/model/status';
import { User } from '@/model/user';
import { cleanUserFromCognito, getCurrentUser, revokeTokens, signUp } from '@/service/cognitoService';
import { federatedRegistration } from '@/service/crmService';
import { InputValidator } from '@/utils';

const ADULT_AGE = 18;

export default defineComponent({
  name: 'RegistrationPanel',
  components: {
    Button,
    Input,
    PhoneNumber,
    Select,
    Switch,
    Message,
    IconArrowLeft,
    Logo,
    PasswordInput
  },
  data() {
    return {
      user: {
        email: '',
        phoneNumber: '',
        password: '',
        name: '',
        surname: '',
        birthdate: '',
        profiling: '',
        marketing: '',
        third: '',
        clientId: ''
      } as User,
      phoneNumber: {
        countryCode: '',
        number: ''
      },
      confirmPassword: '',
      dayBirthdate: this.$t('message.dd'),
      monthBirthdate: this.$t('message.mm'),
      yearBirthdate: this.$t('message.yyyy'),
      defaultYearBirthdateText: this.$t('message.yyyy'),
      defaultYearBirthdateValue: this.$t('message.defaultYearBirthdate'),
      dateCollector: dateCollector,
      acceptProfilingPermission: false,
      acceptMarketingPermission: false,
      acceptThirdPermission: false,
      signUpSucced: false,
      signUpError: false,
      messageText: '',
      isValidPassword: false,
      federatedSignIn: false,
      username: '',
      countryCode: '',
      marketingTextIsExpanded: false,
      profilingTextIsExpanded: false,
      thirdTextIsExpanded: false
    };
  },
  computed: {
    ...mapGetters([
      'global',
      'signUpMessages',
      'clientID',
      'redirectURI'
    ]),

    passwordsMatch(): boolean {
      return this.user.password === this.confirmPassword;
    },
    emailError(): boolean {
      return this.user.email ? !InputValidator.validateEmail(this.user.email) : false;
    },
    nameError(): boolean {
      return this.user.name ? !InputValidator.validateName(this.user.name) : false;
    },
    surnameError(): boolean {
      return this.user.surname ? !InputValidator.validateSurname(this.user.surname) : false;
    },
    birthdateError(): boolean {
      return this.dayBirthdate === 'GG' || this.monthBirthdate === 'MM' || this.yearBirthdate === 'YYYY';
    },
    registrationButtonDisabled(): boolean {
      return !this.user.email ||
        !this.user.name ||
        !this.user.surname ||
        !this.user.password ||
        !this.isValidPassword ||
        !this.passwordsMatch ||
        this.emailError ||
        this.nameError ||
        this.surnameError ||
        this.birthdateError;
    },
    federatedRegistrationButtonDisabled(): boolean {
      return !this.user.email ||
        !this.user.name ||
        !this.user.surname ||
        this.emailError ||
        this.nameError ||
        this.surnameError ||
        this.birthdateError;
    }
  },
  async beforeMount() {
    if (this.$route.name === ROUTES_NAMES.federatedLogin) {
      this.federatedSignIn = true;
      try {
        const { username, attributes } = await getCurrentUser();
        this.username = username;
        this.user.email = attributes.email;
        this.user.name = attributes.name;
        this.user.surname = attributes.family_name;
      } catch (error) { }
    }
  },
  methods: {
    ...mapActions(['setAppStatus', 'setMyEmail', 'startLoading', 'endLoading']),

    async onSignUpPress() {
      try {
        this.messageText = '';
        this.signUpError = false;
        this.user.birthdate = this.formatBirthdate();
        if (!this.isValidBirthdate()) {
          this.messageText = this.$t('message.invalid_birthdate');
          this.signUpError = true;
          return;
        }
        if (!this.isValidAge()) {
          this.messageText = this.$t('message.under_age');
          this.signUpError = true;
          return;
        }
        if (this.phoneNumber.number) {
          this.user.phoneNumber = this.phoneNumber.countryCode + this.phoneNumber.number;
        }

        this.user.profiling = String(this.acceptProfilingPermission);
        this.user.marketing = String(this.acceptMarketingPermission);
        this.user.third = String(this.acceptThirdPermission);
        this.user.email = this.user.email.toLowerCase();
        this.user.name = this.user.name.toUpperCase();
        this.user.surname = this.user.surname.toUpperCase();
        this.startLoading();
        await signUp(this.user);
        this.signUpSucced = true;
      } catch (error: any) {
        this.signUpError = true;
        if (error.code === CognitoException.UsernameExistsException) {
          this.setAppStatus(AppStatus.UserAlreadyExists);
          this.switchToLoginPanel();
        } else if (error.code === CognitoException.InvalidPasswordException ||
          (error.code === CognitoException.InvalidParameterException && error.message.includes('password'))) {
          this.messageText = this.$t('message.invalid_password');
        } else if (error.code === CognitoException.UserLambdaValidationException &&
          error.message.includes(CognitoException.HatTrickSignUpException)) {
          this.messageText = this.$t('message.hat_trick_message');
        } else if (error.code === CognitoException.UserLambdaValidationException &&
          error.message.includes(CognitoException.CRMEmailExistedException)) {
          this.messageText = this.$t('message.crm_existing_email');
        } else if (error.code === CognitoException.UserLambdaValidationException &&
          error.message.includes(CognitoException.CustomMessageTriggerException)) {
          this.signUpSucced = true;
        } else if (error.code === CognitoException.InvalidParameterException &&
          error.message.includes(CognitoException.InvalidPhoneNumber)) {
          this.messageText = this.$t('message.invalid_phone_number');
        } else if (error.code === CognitoException.UserLambdaValidationException &&
          error.message.includes(CognitoException.CRMPhoneNotAllowed)) {
          this.messageText = this.$t('message.crm_phone_not_allowed');
        } else if (error.code === CognitoException.UserLambdaValidationException &&
          error.message.includes(CognitoException.CRMPhoneUsed)) {
          this.messageText = this.$t('message.crm_phone_used');
        } else if (error.code === CognitoException.UserLambdaValidationException &&
          error.message.includes(CognitoException.CRMPhoneNotCongruent)) {
          this.messageText = this.$t('message.crm_phone_not_congruent');
        } else if (error.code === CognitoException.UserLambdaValidationException &&
          error.message.includes(CognitoException.CRMIncongruentData)) {
          this.messageText = this.$t('message.crm_incongruent_data');
        } else {
          this.messageText = this.$t('message.sign_up_error');
        }
      } finally {
        this.endLoading();
        this.user.phoneNumber = '';
      }
    },
    async onFederatedSignUpPress() {
      try {
        this.messageText = '';
        this.signUpError = false;
        this.user.birthdate = this.formatBirthdate();
        if (!this.isValidBirthdate()) {
          this.messageText = this.$t('message.invalid_birthdate');
          this.signUpError = true;
          return;
        }
        if (!this.isValidAge()) {
          this.messageText = this.$t('message.under_age');
          this.signUpError = true;
          return;
        }
        if (this.phoneNumber.number !== '') {
          this.user.phoneNumber = this.phoneNumber.countryCode + this.phoneNumber.number;
        }

        this.user.clientId = this.clientID;
        this.user.profiling = String(this.acceptProfilingPermission);
        this.user.marketing = String(this.acceptMarketingPermission);
        this.user.third = String(this.acceptThirdPermission);
        this.user.email = this.user.email.toLowerCase();
        this.user.name = this.user.name.toUpperCase();
        this.user.surname = this.user.surname.toUpperCase();
        this.startLoading();
        await federatedRegistration(this.user, this.username);
      } catch (error: any) {
        this.signUpError = true;
        console.log(`TEST${error.response?.data?.message}`);
        if (error.response?.data?.message?.includes(CognitoException.HatTrickFederatedSignUpException)) {
          this.messageText = this.$t('message.hat_trick_message');
        }
        else if (error.response?.data?.message?.includes(CognitoException.CRMEmailExistedException)) {
          this.messageText = this.$t('message.crm_existing_email');
        }
        else if (error.response?.data?.message?.includes(CognitoException.CRMPhoneNotAllowed)) {
          this.messageText = this.$t('message.crm_phone_not_allowed');
        } 
        else if (error.response?.data?.message?.includes(CognitoException.CRMPhoneUsed)) {
          this.messageText = this.$t('message.crm_phone_used');
        } 
        else if (error.response?.data?.message?.includes(CognitoException.CRMPhoneNotCongruent)) {
          this.messageText = this.$t('message.crm_phone_not_congruent');
        } 
        else if (error.response?.data?.message?.includes(CognitoException.CRMIncongruentData)) {
          this.messageText = this.$t('message.crm_incongruent_data');
        } 
        else {
          this.messageText = this.$t('message.sign_up_error');
        }
      } finally {
        this.endLoading();
        this.user.phoneNumber = '';
      }
    },
    async switchToLoginPanel() {
      if (this.user.email) {
        this.setMyEmail(this.user.email);
      }

      if (this.federatedSignIn) {
        try {
          await cleanUserFromCognito();
        } catch (error) {
          console.error(error);
        }
        await revokeTokens();
        return this.$router.push({
          path: 'login',
          query: {
            client_id: this.clientID,
            redirect_uri: this.redirectURI
          }
        });
      }

      this.$router.push({
        path: 'login',
        query: { ...this.$route.query }
      });
    },
    formatBirthdate(): string {
      return this.$t('message.date_format', {
        day: Number(this.dayBirthdate) < 10 ? `0${this.dayBirthdate}` : this.dayBirthdate,
        month: Number(this.monthBirthdate) < 10 ? `0${this.monthBirthdate}` : this.monthBirthdate,
        year: this.yearBirthdate
      });
    },
    isValidBirthdate(): boolean {
      return moment(this.user.birthdate).isValid();
    },
    isValidAge(): boolean {
      return moment().diff(this.user.birthdate, 'years') >= ADULT_AGE;
    },
    updatePasswordStatus(isValid: boolean) {
      this.isValidPassword = isValid;
    }
  },
});
